//import React from "React"
import { events } from "@/common/constants";
import { misc } from "@/common/theme";
import { TransitionLink } from "@/mono/components/TransitionLink";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import styled, { useTheme } from "styled-components";
import Badge from "../Badge";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { isMobileLayout, mediaMobile } from "@/mono/lib/responsive";
import BorderedIcon from "../BorderedIcon";
import useAnimation, {
  useCSSClassTransition,
} from "@/mono/lib/hooks/useAnimation";
import { EASE_CUSTOM_2, easeInExpo } from "@/mono/lib/styles/easing";
import useEvent from "@/mono/lib/hooks/useEvent";
import emitter from "@/mono/lib/emitter";
import useResize from "@/mono/lib/hooks/useResize";
import { Divider } from "../SectionTitle";
import useDelayedLoaderAnimation from "@/mono/lib/hooks/useDelayedLoaderAnimation";
import AnimatedHoverText from "../AnimatedHoverText";
import CustomSplitText from "@/mono/components/CustomSplitText";
import { useIsClient } from "@/mono/lib/hooks/useIsClient";
import Logo from "../Logo";
import Workers from "mono/lib/webworkers/Workers";
import SplitText from "mono/components/SplitText";

export const getNavigationHeight = (isMobileLayout) => {
  return isMobileLayout ? "180rem" : "150rem";
};

const MobileMenuIcon = memo(() => {
  const wrapper = useRef();
  const bar1 = useRef();
  const bar2 = useRef();
  const [animatedIn, setAnimatedIn] = useState(false);

  // const { animateIn: animateInCSS, animateOut: animateOutCSS } =
  //   useCSSClassTransition({
  //     element: wrapper,
  //     className: "active",
  // });

  const animateIn = useCallback(() => {
    setAnimatedIn(true);
  }, []);

  const animateOut = useCallback(() => {
    setAnimatedIn(false);
  }, []);

  useEvent(events.MENU_OPEN, () => {
    animateOut();
  });
  useEvent(events.MENU_CLOSE, () => {
    animateIn();
  });

  // useEvent(events.ROUTE_CHANGE_START, () => {
  //   animateOut();
  // });
  useEvent(events.ROUTE_CHANGE_COMPLETE, () => {
    if (!animatedIn) animateIn();
  });

  useDelayedLoaderAnimation(() => {
    animateIn();
  }, 500);

  return (
    <StyledMenuIcon
      ref={wrapper}
      onClick={() => emitter.emit(events.MENU_OPEN)}
      className={animatedIn ? "active" : ""}
    >
      <span ref={bar1}></span>
      <span ref={bar2} className="bar2"></span>
    </StyledMenuIcon>
  );
});

export default function NavigationBar() {
  const border = useRef();
  const emitter = useGlobalStore((s) => s.emitter);
  const logo = useRef();
  const [badge1, badge2] = [useRef(), useRef()];
  const menuText = useRef();
  const logoTextRef = useRef();

  const theme = useTheme();

  // HINT: (temporary)? solution to avoid animating out once theme switches
  // has nothing to do with theme, but it resets the in animation bc of re-render
  // const [isMobile, setIsMobile] = useState(isMobileLayout());

  // useResize(() => {
  //   const currentIsMobile = isMobileLayout();
  //   if (currentIsMobile !== isMobile) setIsMobile(currentIsMobile);
  // });

  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  // const isVisible = useRef(false);

  const [badgesVisible, setBadgesVisible] = useState(false);

  function animateIn() {
    if (logoTextRef.current) logoTextRef.current.animateIn?.();

    border.current.animateIn();

    badge1.current?.animateIn?.();
    badge2.current?.animateIn?.({ delay: 0.2 });

    if (logo.current) logo.current.animateIn();
    menuText.current?.animateIn?.();
    setBadgesVisible(true);

    //    isVisible.current = true;
  }

  useDelayedLoaderAnimation(() => {
    animateIn();
  }, 0);

  useEvent(events.SWITCH_THEME, (theme) => {
    Workers.setGLBackgroundTheme(theme);
  });

  return (
    <StyledNavigation>
      <TransitionLink href="/">
        {isMobileLayout ? (
          <Logo ref={logo} />
        ) : (
          <SplitText
            ref={logoTextRef}
            className="nav-logo"
            animateOnScroll={false}
            type="chars"
            tag="h2"
            animation="charClipped"
          >
            Nine Creative Agency
          </SplitText>
        )}
      </TransitionLink>
      {!isMobileLayout && (
        <AnimatedHoverText
          ref={menuText}
          className="nav-menu"
          onClick={() => {
            emitter.emit(events.MENU_OPEN);
          }}
        >
          Menu
        </AnimatedHoverText>
      )}
      <ThemeSwitcher>
        <Badge
          ref={badge1}
          className={badgesVisible ? "active" : ""}
          selected={theme.theme.__TYPE === "light"}
          onClick={() => emitter.emit(events.SWITCH_THEME, "light")}
        >
          Light
        </Badge>
        <Badge
          ref={badge2}
          className={badgesVisible ? "active" : ""}
          selected={theme.theme.__TYPE === "dark"}
          onClick={() => emitter.emit(events.SWITCH_THEME, "dark")}
        >
          Dark
        </Badge>
      </ThemeSwitcher>
      {isMobileLayout && <MobileMenuIcon></MobileMenuIcon>}
      <Divider className="navigation__border-bottom" ref={border} />
    </StyledNavigation>
  );
}

const StyledNavigation = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  height: ${misc.navigationHeight("fenhuhfe")};
  /* border-bottom: 1px solid ${({ theme }) => theme.theme.textColor}; */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: ${(props) => props.theme.misc.pagePadding()};
  padding-right: ${(props) => props.theme.misc.pagePadding()};
  background-color: ${({ theme }) => theme.theme.background};
  z-index: 5;

  .logo {
    width: 200rem;
    path {
      fill: ${({ theme }) => theme.theme.textColor};
    }
  }

  .navigation__border-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
  }

  /* opacity: 0; */

  h2 {
    ${({ theme: { locale } }) => setTypography("smallParagraph", locale)};
    text-align: center;
    cursor: pointer;
    color: ${({ theme }) => theme.theme.textColor};
  }

  .nav-menu {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .nav-logo {
    line-height: 1.1;
    ${mediaMobile(`
      font-weight: 900;
      font-size: 84rem;
    `)}
  }

  /* .theme-badge__dark .txt-shuffle {
    color: ${({ theme }) => theme.theme.background};
  } */
`;

const ThemeSwitcher = styled.div`
  display: flex;
  gap: 10px;

  p {
    cursor: pointer;
  }

  .txt-shuffle {
  }

  ${mediaMobile(`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `)}
`;

const StyledMenuIcon = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;

  width: 80rem;
  --bar-height: 3px;
  height: calc(60rem - var(--bar-height) * 2);

  span {
    width: 100%;
    height: var(--bar-height);
    background-color: ${({ theme }) => theme.theme.textColor};
    transform: scaleX(0);
    transition: transform 0.3s ${easeInExpo};
    transform-origin: left;
  }

  .bar2 {
    transition-delay: 0.2s;
  }

  &.active {
    transform-origin: right;

    span {
      transform: scaleX(1);
    }
  }
`;
